import { useEffect, useState } from "react";
import { getAudit, getAuditDetail } from "../lib/api";
import { DataTable } from "../components/DataTable";
import { EvidencePanel } from "../components/EvidencePanel";
import { ResultNumbers } from "../components/ResultNumbers";
import { TrustBadge } from "../components/TrustBadge";
import { ErrorBox, Loading, PageHeader } from "../components/Shell";
import { num } from "../lib/format";

export default function AuditPage() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    getAudit(100).then(setData).catch(setErr);
  }, []);

  if (err) return <ErrorBox error={err} />;
  if (!data) return <Loading label="Loading the audit log" />;

  return (
    <div>
      <PageHeader overline="Query audit log" title="Every answer, replayable" testId="audit-header">
        {num(data.count)} answers logged, each replayable with its intent, metrics and badge.
      </PageHeader>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] gap-10 items-start">
        <div className="min-w-0">
          <DataTable
            testId="audit-table"
            maxHeight="max-h-[70vh]"
            rows={data.rows}
            onRowClick={(r) => {
              setDetail(null);
              getAuditDetail(r.answer_id).then(setDetail).catch(setErr);
            }}
            columns={[
              { key: "question", label: "Question", render: (r) => (
                <div>
                  <div className="text-stone-900">{r.question}</div>
                  <div className="num text-[12px] text-stone-600 mt-0.5">
                    {r.created_at?.slice(0, 19).replace("T", " ")} · {r.kind} · {r.compute_ms} ms ·
                    registry v{r.registry_version}
                  </div>
                </div>
              )},
              { key: "badge", label: "Badge", align: "right", render: (r) => (
                <span className={`text-[12px] font-semibold px-1.5 py-0.5 rounded ${
                  r.answerability?.trust_badge === "High" ? "bg-emerald-100 text-emerald-800"
                    : r.answerability?.trust_badge === "Directional" ? "bg-amber-100 text-amber-800"
                    : "bg-rose-100 text-rose-800"}`}>
                  {r.answerability?.trust_badge || "—"}
                </span>
              )},
              { key: "n", label: "n", align: "right",
                render: (r) => num(r.answerability?.sample_size) },
            ]}
          />
        </div>

        <div className="min-w-0 xl:sticky xl:top-[76px] space-y-3">
          {detail ? (
            <>
              <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-3"
                   data-testid="audit-detail">
                <div className="text-sm font-medium text-stone-900">{detail.question}</div>
                <TrustBadge answerability={detail.answerability} testId="audit-detail-trust" />
                <div>
                  <div className="text-[12px] font-semibold text-stone-600 mb-1">
                    Narration produced
                  </div>
                  <p className="text-xs text-stone-800 leading-relaxed whitespace-pre-wrap"
                     data-testid="audit-narrative">
                    {detail.narrative || "— not narrated —"}
                  </p>
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-stone-600 mb-1">
                    Parsed intent
                  </div>
                  <pre className="num text-[12px] bg-stone-50 border border-stone-200 rounded p-2 overflow-auto whitespace-pre-wrap">
                    {JSON.stringify(detail.intent, null, 2)}
                  </pre>
                </div>
              </div>
              <EvidencePanel result={detail.result} testId="audit-evidence">
                <ResultNumbers result={detail.result} />
              </EvidencePanel>
            </>
          ) : (
            <div className="border border-dashed border-stone-300 rounded-xl p-8 text-center">
              <p className="text-sm text-stone-600 leading-relaxed">
                Pick a logged answer to replay its intent, numbers, evidence and narration.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
